import React from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  Form,
  Link,
  useLoaderData,
  useSearchParams,
} from 'react-router-dom';
import type {
  ProjectsPage,
  CollectionProgress,
} from '@tinypro-audit/contracts';
import { projectReviewCategory } from '@tinypro-audit/contracts';
import { ReviewCategoryBadge } from '../components/ReviewCategoryBadge';
import { ProjectsNavigation } from '../components/ProjectsNavigation';
import { usePageRefresh } from '../api/use-page-refresh';

const date = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '—';
const text = (value: unknown) => (typeof value === 'string' ? value : '');
export const collectionLabels: Record<CollectionProgress['state'], string> = {
  selected: 'Waiting for collector',
  collecting: 'Collecting evidence',
  paused: 'Collection paused',
  blocked: 'Collection blocked',
  cancelled: 'Collection cancelled',
  uploading: 'Uploading evidence',
  upload_expired: 'Upload interrupted',
  upload_rejected: 'Upload rejected',
};
export const collectionColor = (state: CollectionProgress['state']) =>
  ['blocked', 'upload_rejected', 'upload_expired'].includes(state)
    ? 'orange'
    : state === 'paused' || state === 'cancelled'
      ? 'gray'
      : 'blue';

export function ProjectAuditsPage(): React.ReactElement {
  const data = useLoaderData() as ProjectsPage;
  const [params, setParams] = useSearchParams();
  const offset = Number(params.get('offset') ?? 0);
  usePageRefresh(data.refresh, undefined, false);
  const page = (next: number) => {
    const values = new URLSearchParams(params);
    values.set('offset', String(next));
    setParams(values);
  };
  return (
    <Stack>
      <ProjectsNavigation view="projects" />
      <Group justify="space-between">
        <div>
          <Text c="dimmed">
            Projects appear as soon as collection starts. Processing progress
            and review outcomes are shown separately.
          </Text>
        </div>
        <Button component="a" href="/api/projects/export.csv" variant="default">
          Export CSV
        </Button>
      </Group>
      {!data.worker.configured && (
        <Alert color="yellow">
          {data.worker.last_seen_at
            ? 'The worker is connected but its AI credential is not configured.'
            : 'No recent worker heartbeat. Accepted evidence remains queued until the worker is available.'}
        </Alert>
      )}
      <Form method="get">
        <Group align="end">
          <TextInput
            name="search"
            label="Project number or ServiceTitan project ID"
            defaultValue={params.get('search') ?? ''}
          />
          <Button type="submit">Search</Button>
        </Group>
      </Form>
      <Table.ScrollContainer minWidth={760}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Project</Table.Th>
              <Table.Th>Progress</Table.Th>
              <Table.Th>Review</Table.Th>
              <Table.Th>Orders verified</Table.Th>
              <Table.Th>Updated</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.items.map((p) => (
              <Table.Tr key={p.project_id}>
                <Table.Td>
                  <Anchor component={Link} to={`/projects/${p.project_id}`}>
                    Project {p.display_number}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    ID {p.project_id}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={
                      p.collection
                        ? collectionColor(p.collection.state)
                        : p.state === 'failed'
                          ? 'red'
                          : p.state === 'succeeded'
                            ? 'green'
                            : 'blue'
                    }
                  >
                    {p.collection
                      ? collectionLabels[p.collection.state]
                      : p.state === 'succeeded'
                        ? 'Analysis complete'
                        : p.state === 'failed'
                          ? 'Analysis failed'
                          : p.state === 'queued'
                            ? 'Queued for analysis'
                            : p.state === 'running'
                              ? 'Analyzing'
                              : p.state === 'retry_wait'
                                ? 'Analysis retry scheduled'
                                : 'Awaiting evidence'}
                  </Badge>
                  <Text size="xs">
                    {p.collection?.note ||
                      (!p.collection && text(p.progress?.message))}
                  </Text>
                  {p.collection?.state === 'paused' && (
                    <Text size="xs">No collector update in the last hour.</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <ReviewCategoryBadge category={p.is_current && p.human_review_complete ? 'reviewed' : projectReviewCategory(p)} />
                  {!p.is_current && (
                    <Text size="xs" c="dimmed">
                      Current evidence not yet reviewed
                    </Text>
                  )}
                  {p.collection && p.latest_submission_id && (
                    <Text size="xs" c="dimmed">
                      Review of last accepted evidence
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {p.is_current && p.last_result?.workflow
                    ? `${p.last_result.workflow.orders.filter((o) => o.verification === 'verified').length} / ${p.last_result.workflow.orders.length}`
                    : '—'}
                </Table.Td>
                <Table.Td>{date(p.updated_at)}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {!data.items.length && (
        <Paper p="xl" withBorder>
          <Text>
            No matching projects. Projects appear here when the collector
            selects them.
          </Text>
          <Text size="sm" c="dimmed">
            Create a collector key in Admin → API keys to begin.
          </Text>
        </Paper>
      )}
      <Group justify="space-between">
        <Text size="sm">{data.total} projects</Text>
        <Group>
          <Button
            variant="default"
            disabled={offset === 0}
            onClick={() => page(Math.max(0, offset - 50))}
          >
            Previous
          </Button>
          <Button
            variant="default"
            disabled={offset + 50 >= data.total}
            onClick={() => page(offset + 50)}
          >
            Next
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
